import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-head font-semibold uppercase text-2xl lg:text-3xl text-[#2D2D2D] leading-tight">{title}</h2>
      <div className="article-body mt-3 text-[#1E1E1E]">{children}</div>
    </section>
  );
}

export default function About() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-12" data-testid="about-page">
      <Seo title="About Kyto" description="VoluntoldHQ is for the coach who didn't exactly volunteer. Meet Kyto — 42 years around sports, a Certified Sport Psychology Coach (CSPC) coaching the mental side of youth sports." path="/about" />
      <img src="/brand/logos/voluntoldhq-logo-full-on-light.svg" alt="VoluntoldHQ" className="w-full max-w-[360px] h-auto mb-6" />
      <span className="kicker">About</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-4xl lg:text-6xl text-[#1E1E1E] leading-[1.05]">
        Somebody talked you into this. Now let's make you good at it.
      </h1>

      {/* Intro + headshot */}
      <div className="mt-8 grid sm:grid-cols-[1fr_260px] gap-8 items-start">
        <div className="article-body text-[#1E1E1E]">
          <p className="text-lg font-semibold text-[#222222]">
            Former GM. 20-year coach. I show you what the player, the parent, and the coach each see in the same moment.
          </p>
          <p>
            VoluntoldHQ is for the coach who didn't exactly volunteer — the parent whose hand went up a beat too slow,
            the assistant who inherited a clipboard, the tenured coach still looking for a sharper edge. I've been all
            three.
          </p>
        </div>
        <figure className="sm:sticky sm:top-24 flex flex-col items-center" data-testid="about-headshot">
          <div className="relative w-56 h-56">
            <img src="/about-kyto.jpg" alt="Kyto Khouang, founder of VoluntoldHQ" loading="lazy"
              className="w-56 h-56 rounded-full object-cover border-4 border-[#2D2D2D]" />
            <img src="/cspc-badge.png" alt="Certified Sport Psychology Coach — Spencer Institute" loading="lazy"
              className="absolute -bottom-3 -right-3 h-24 w-24 rounded-full object-cover bg-[#F3F1EC] border-2 border-[#2D2D2D] shadow-md" data-testid="about-cspc-badge" />
          </div>
          <figcaption className="mt-6 text-center">
            <span className="font-head font-semibold uppercase tracking-wide text-lg text-[#1E1E1E]">Kyto Khouang</span>
            <span className="block text-sm text-[#6E6E6E]">CSPC</span>
            <span className="block mt-2 text-xs text-[#6E6E6E] leading-snug">
              Certified Sport Psychology Coach<br />Spencer Institute (CSPC)<br />
              B.S. in Psychology, University of Arizona Global
            </span>
          </figcaption>
        </figure>
      </div>

      <Section title="Both Sides of the Whistle">
        <p>
          I've been around sports my whole life, whether I was playing or coaching. I've based 42 years of my life
          around it. Now I want to share what I've experienced and what I've learned — not really a how-to, more just
          how I see it, from a different perspective than most.
        </p>
        <p>
          I played almost every sport growing up, but football was always the one that had me. After high school, I had
          a few offers to keep playing and ended up in the Rocky Mountain Football League and the Utah Indoor Football
          League — adult leagues for guys who weren't ready to walk away from the game. I played for over 15 years
          before I decided it was time to focus on my career and start a family. That's when I started coaching youth
          football, to fill the emptiness that came with stepping away from playing. Somewhere in there I also ended up
          running a football team at the semi-pro level as GM, which taught me as much about building a program as
          playing or coaching ever did.
        </p>
        <p>
          Since then, I've moved from little league DC and OC, up through JV, and now onto the varsity coaching staff.
          I've coached just about every sport along the way — tball, soccer, volleyball, football. Right now I'm the
          varsity DB/WR coach at my high school, I coach my daughter's volleyball team, I'm a soccer dad on the
          sidelines the rest of the time, and I coach my son's tball team. Competition's still an itch, so I stay active
          playing rec league adult softball, volleyball, and basketball.
        </p>
        <p>
          I've got a Bachelor's in Psychology with a minor in Organizational Management from University of Arizona
          Global, and I'm a Certified Sport Psychology Coach (CSPC) through the Spencer Institute — the part of the game
          that lives between a kid's ears. I also lead a sales and merchandising team in my day job. Same approach either
          way: you're building people, not just hitting numbers.
        </p>
        <p>
          None of that matters unless it helps you run a better practice on Tuesday and have a calmer sideline on
          Saturday.
        </p>
      </Section>

      <Section title="What I've Seen">
        <p>
          There's no shortage of youth sports resources out there. What's missing is anyone tackling the mental side, or
          the generational gap between coaches, players, and parents — which is exactly where I live. I've seen the same
          things play out over and over:
        </p>
        <p>
          After a rookie coach got thrown into a season with zero training and nobody checking on him. After a kid quit a
          sport he used to love because of how a coach talked to him. After a player got overlooked — not because he
          wasn't good enough, but because of what his last coach said about him.
        </p>
        <p>
          None of that is really anyone's fault. It's just what happens when nobody hands a volunteer coach a playbook
          for the part of the job that actually matters.
        </p>
      </Section>

      <Section title="What I Believe">
        <p>
          The biggest gap in youth sports isn't talent. It's training and retaining the coach and the player.
        </p>
        <p>
          Youth sports has an adult problem. Officials, umpires, coaches, parents — all of it — take things too far, and
          it ends up making the whole thing less fun for the kids it's supposed to be for.
        </p>
        <p>I see it show up in three places:</p>
        <ul>
          <li><strong>In their training</strong> — most youth coaches get a whistle and a roster. Not a curriculum.</li>
          <li><strong>In their confidence</strong> — first-year coaches second-guess everything because nobody told them that's normal.</li>
          <li><strong>In their second chance</strong> — a kid who gets labeled early rarely gets a fair look again.</li>
        </ul>
      </Section>

      <Section title="The Bridge">
        <p>
          I got coached by people who cared more about how my uniform looked than anything else. Military-style, rah-rah,
          that whole approach. The moment that actually changed how I think about coaching was when a coach wrote on my
          goals sheet that I needed to watch who I was hanging around with. He didn't know me. He had a perception of me,
          and he coached off that instead of who I actually was.
        </p>
        <p>
          I grew up under that style and became a millennial coach. Now I'm coaching Alpha and Gen Z kids, and dealing
          with the parents raising them, and a lot of what I learned doesn't translate over without some work.
        </p>
        <p>
          I don't think it's old-school vs. new-school, like one side has to win. I think it's about someone who's
          actually stood in both rooms, who can carry what's still worth keeping from the old way into how I coach now —
          for the kids on the field and their parents on the sideline.
        </p>
      </Section>

      <Section title="What You'll Actually Get">
        <p>
          Real moments from the bench. Lived experience before advice. And a few quiet tools that do actual work — a
          readiness assessment, a practice-plan builder, a script for the conversation you've been dreading. Everything
          personalized comes through <strong>Scout</strong>, the recommendation brain that reads your answers and points
          you at the one next step that matters.
        </p>
        <p>No funnels. No hype. One coaching read a week, and tools that respect your time.</p>
        <p><strong>Glad you're here.</strong></p>
        <p>
          Follow along{" "}
          <a href="https://instagram.com/voluntoldhq" target="_blank" rel="noopener noreferrer" className="link-line text-[#2D2D2D] font-semibold">@voluntoldhq</a>.
        </p>
      </Section>

      <p className="mt-10 kicker">VoluntoldHQ — For Rookie Coaches &amp; Youth Sports Parents</p>

      <div className="mt-8 flex flex-wrap gap-4 border-t border-[#D3CFC0] pt-8">
        <Link to="/assessment" data-testid="about-assessment-link" className="bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#242424] transition-colors">
          See where you stand
        </Link>
        <Link to="/products" className="border-2 border-[#2D2D2D] text-[#2D2D2D] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#2D2D2D] hover:text-[#F3F1EC] transition-colors">
          Digital products
        </Link>
      </div>
    </div>
  );
}
